import Offcanvas from "react-bootstrap/Offcanvas";
import { Card } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";
import WebService from "../../utility/WebService";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

/* ================= PROPS ================= */

interface Feature {
    _id: string;
    key: string;
    title: string;
    description?: string | null;
    dataType?: string;
    meta?: object;
    sortOrder?: number;
    isActive?: boolean;
    sectionId?: string; // we'll keep this for convenience
}

interface SectionItem {
    sectionId: {
        _id: string;
        title: string;
    };
    features: Feature[];
    sortOrder?: number;
}

interface Props {
    show: boolean;
    onHide: () => void;
    section: SectionItem | null;
    onSaved: () => void;
}

/* ================= COMPONENT ================= */

const SortFeaturesOffcanvas = ({
    show,
    onHide,
    section,
    onSaved,
}: Props) => {
    const [rows, setRows] = useState<Feature[]>([]);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    /* Load features when section changes */
    useEffect(() => {
        if (section) {
            setRows(section.features);
            setHasChanges(false); // reset on open
        } else {
            setRows([]);
        }
    }, [section]);

    const columns = [
        {
            id: "title",
            label: "",
            minWidth: 250,
        },
    ];

    /* 🔥 ONLY LOCAL REORDER (NO API CALL HERE) */
    const handleReorder = (newRows: Record<string, any>[]) => {
        setRows(newRows as Feature[]);
        setHasChanges(true);
    };

    /* ✅ SAVE BUTTON HANDLER */
    const saveOrder = () => {
        if (!section || !hasChanges) return;

        setSaving(true);

        const payload = rows.map((item, index) => ({
            id: item._id,
            sortOrder: index + 1,
        }));

        WebService.putAPI({
            action: "memberShipBenifits/update-feature-order",
            body: {
                sectionId: section.sectionId._id,
                features: payload,
            },
        })
            .then(() => {
                toast.success("Feature order saved successfully");
                onSaved();     // reload parent
                onHide();      // close offcanvas
            })
            .catch(() => {
                toast.error("Failed to save feature order");
            })
            .finally(() => {
                setSaving(false);
                setHasChanges(false);
            });
    };

    if (!section) return null;

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: "60%" }}>
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                   {section.sectionId.title}
                </Offcanvas.Title>
            </Offcanvas.Header>

            <Offcanvas.Body>
                <Card className="p-3 sort-feature-table">
                    <CustomTableSortable
                        columns={columns}
                        rows={rows}
                        totalCount={rows.length}
                        currentPage={1}
                        pageSize={rows.length}
                        isLoading={saving}
                        draggable
                        onRowsReorder={handleReorder}
                        onPageChange={() => { }}
                    />
                </Card>

                {/* ACTION BUTTONS */}
                <div className="d-flex justify-content-end gap-2 mt-3">
                    <CustomButton
                        label="Cancel"
                        isLoading={false}
                        onClick={onHide}
                    />

                    <CustomButton
                        label="Save Order"
                        isLoading={saving}
                        onClick={saveOrder}
                        disabled={!hasChanges}
                    />
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default SortFeaturesOffcanvas;
